import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
    EnrollPatientRequestModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel,
    EnterpriseCoordinatorAccountUpsearchRequestModel,
    EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    EnterpriseCoordinatorSharedAddressUpsearchModel,
    EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
    EnterpriseCoordinatorSharedPhoneUpsearchModel,
    UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
    UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
    UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
    EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
} from '@trialcard/apigateway.models';
import { Gender, RoleType } from '@trialcard/enums';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';
import { Action, Mutation } from 'vuex-module-decorators';

import { EdgeEnrollmentApi } from '@trialcard/apigateway.client';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import moment, { Moment } from 'moment';
import { PDFDocument } from 'pdf-lib';
import { EdgeDocumentsApi } from '@trialcard/apigateway.client/edgeDocumentsApi';
import { string } from 'yargs';
import { Settings } from '~/services/settings';
import SurveyModule from '~/store/survey';
import genders from '~/assets/genders';
import PharmacyEligibilityModule from '~/store/pharmacy-eligibility';
import EligibilityModule from '~/store/eligibility';

@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
    @Resolve
    public patientEnrollmentApi!: EdgePatientEnrollmentApi;

    @Resolve
    public enrollmentApi!: EdgeEnrollmentApi;

    @Resolve
    public settings!: Settings;

    @Resolve
    public survey!: SurveyModule;

    @Resolve
    edgeDocumentsApi!: EdgeDocumentsApi;

    @Resolve
    public hcpPharmacyeligibility!: PharmacyEligibilityModule;

    @Resolve
    public patientEligibility!: EligibilityModule;

    defaultPatientInformationData = {
        firstName: '' as string,
        lastName: '' as string,
        dateOfBirth: null as Moment | null,
        addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
        gender: null as Gender | null,
    };

    defaultCareGiverInformationData = {
        caregiverName: '' as string,
        relationShipToPatient: '' as string,
        // addresses: [] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
        emailAddresses: [] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
        phoneNumbers: [] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
    };

    defaultPrescriberData = [
        {
            model: {
                firstName: '',
                lastName: '',
                npi: '',
                accountRelationshipIsPrimary: true,
                prescriberLicenses: [],
                externalIds: [],
                addresses: [],
                phoneNumbers: [],
                emailAddresses: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[];

    defaultSiteData = [
        {
            canUpdate: true,
            canInsert: true,
            skipSearch: false,
            model: {
                accountRelationshipIsPrimary: true,
                siteRoleType: RoleType.PrescriberSite,
                pointOfContacts: [],
                description: '',
                addresses: [
                    {
                        addressOne: '',
                        addressTwo: '',
                        zip: '',
                        city: '',
                        state: '',
                    },
                ],
                contacts: [],
                emailAddresses: [],
                externalIds: [],
                phoneNumbers: [],
                globalIds: [],
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel[];

    defaultPayorData = [
        {
            model: {
                payorId: (null as unknown) as number,
                payorType: (null as unknown) as number,
                accountRelationshipIsPrimary: true,
                payorGroup: '',
                payorName: '',
                webAddresses: [],
                addresses: [],
                phoneNumbers: [
                    {
                        phoneType: 1,
                        phoneNumber: '',
                        phonePurpose: 1,
                    },
                    {
                        phoneType: 3,
                        phoneNumber: '',
                        phonePurpose: 8,
                    },
                ],
                emailAddresses: [],
                contacts: [],
                payorBenefit: {
                    dateOfBirth: null as Moment | null,
                    policyHolderFirstName: '',
                    policyHolderLastName: '',
                    relationshipToPatient: undefined,
                    policyNumber: '',
                    planType: undefined,
                },
            },
        },
    ] as UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[];
    // defaultNcpdp = '';

    defaultContactPreferences = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[];
    defaultContacts = [] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[];

    defaultMemberNumbers = {
        medical: 'XXXXXXXXXXX',
        pharmacy: 'XXXXXXXXXXX',
    };
    defaultMemberNumber = 'XXXXXXXXXXX';
    defaultExpirationDate = moment().utc();
    pharmaMemberNumber = this.defaultMemberNumbers.pharmacy;
    medicalMemberNumber = this.defaultMemberNumbers.medical;

    public defaultAuthorizationData = {
        consentToAuthorizationCheckbox: false as boolean,
        tcCheckbox: false as boolean,
        readPrivacyNotice: false as boolean,
        authorizationFirstName: '',
        authorizationLastName: '',
        relationshipToPatient:'',
    };

    public defaultPharmacyAuthorizationData = {
        patientConsentToAuthorization: false as boolean,
        patientAgreeTermsAndConditions: false as boolean,
        patientAgreesPrivacyPolicy: false as boolean,
        authorizationFirstName: '' as string,
        authorizationLastName: '' as string,
        relationshipToPatient: '' as string,
    }

    memberNumber = this.defaultMemberNumber;
    expirationDate = this.defaultExpirationDate;
    patientInformationData = { ...this.defaultPatientInformationData };
    careGiverInformationData = { ...this.defaultCareGiverInformationData };
    prescriberData = [...this.defaultPrescriberData];
    siteData = [...this.defaultSiteData];
    payorData = [...this.defaultPayorData];
    contactPreferences = [...this.defaultContactPreferences];
    AuthorizationData = { ...this.defaultAuthorizationData };
    pharmacyAuthorizationData = { ...this.defaultPharmacyAuthorizationData };
    isCareGiver = false as boolean;
    contacts = [...this.defaultContacts] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[];
    ncpdp = '';
    pharmacySignature = '' as string;
    prescriberSignature = '' as string;

    @Mutation
    public setPharmaMemberNumber(pharmaMemberNumber: string) {
        this.pharmaMemberNumber = pharmaMemberNumber;
    }

    @Mutation
    public setMedicalMemberNumber(medicalMemberNumber: string) {
        this.medicalMemberNumber = medicalMemberNumber;
    }

    @Mutation
    updateAuthorizationData(newAuthorizationData: typeof EnrollmentModule.prototype.defaultAuthorizationData) {
        this.AuthorizationData = { ...newAuthorizationData };
    }

    @Mutation
    updatePharmacyAuthorizationData(newAuthorizationData: typeof EnrollmentModule.prototype.defaultPharmacyAuthorizationData) {
        this.pharmacyAuthorizationData = { ...newAuthorizationData }
    }

    @Mutation
    public async updateContacts(newContacts: EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[]) {
        this.contacts = newContacts;
    }

    @Mutation
    public async updatePatientInformation(newPatientInformation: typeof EnrollmentModule.prototype.patientInformationData) {
        this.patientInformationData = { ...this.patientInformationData, ...newPatientInformation };
    }

    @Mutation
    public async updatePrescribers(newPrescriberData: UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel[]) {
        this.prescriberData = [...newPrescriberData];
    }

    @Mutation
    public async updateSiteData(newSiteData: typeof EnrollmentModule.prototype.defaultSiteData) {
        this.siteData = [...newSiteData];
    }

    @Mutation
    public async updatePayors(newPayorData: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[]) {
        this.payorData = [...newPayorData];
    }

    @Mutation
    public async updateCareGiverInformation(newCareGiverInformation: typeof EnrollmentModule.prototype.careGiverInformationData) {
        this.careGiverInformationData = newCareGiverInformation;
    }

    @Mutation
    public async updateContactPreferences(newContactPreferences: EnterpriseCoordinatorAccountServiceModelsAccountContactPreferenceModel[]) {
        this.contactPreferences = [...newContactPreferences];
    }

    @Mutation
    public setMemberNumber(memberNumber: string) {
        this.memberNumber = memberNumber;
    }

    @Mutation
    public setNcpdp(ncpdp: string) {
        this.ncpdp = ncpdp;
    }

    @Mutation
    public setPharmacySignature(pharmacySignature: string) {
        this.pharmacySignature = pharmacySignature;
    }

    @Mutation
    public setPrescriberSignature(prescriberSignature: string){
        this.prescriberSignature = prescriberSignature;
    }

    @Mutation
    public setExpirationDate(expirationDate: Moment) {
        this.expirationDate = expirationDate;
    }

    @Mutation
    public setIsCareGiver(isCareGiver: boolean) {
        this.isCareGiver = isCareGiver;
    }

    @Action({ rawError: true })
    public clearStore() {
        this.setMemberNumber(this.defaultMemberNumber);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.setExpirationDate(this.defaultExpirationDate);
        this.updatePatientInformation(this.defaultPatientInformationData);
        this.updateCareGiverInformation(this.defaultCareGiverInformationData);
        this.updatePrescribers(this.defaultPrescriberData);
        this.updateSiteData(this.defaultSiteData);
        this.updatePayors(this.defaultPayorData);
        this.updateContactPreferences(this.defaultContactPreferences);
        this.updateAuthorizationData(this.defaultAuthorizationData);
        this.updatePharmacyAuthorizationData(this.defaultPharmacyAuthorizationData)
        this.setIsCareGiver(false);
        this.hcpPharmacyeligibility.clearStore();
        this.patientEligibility.clearStore();
        this.setNcpdp('');
        this.updateContacts(this.defaultContacts);
        this.setPharmacySignature('');
        this.setPrescriberSignature('');
    }

    @Action({ rawError: true })
    public clearEnrollment() {
        this.updatePatientInformation({ ...this.defaultPatientInformationData });
        this.setMemberNumber(this.defaultMemberNumber);
        this.setPharmaMemberNumber(this.defaultMemberNumbers.pharmacy);
        this.setMedicalMemberNumber(this.defaultMemberNumbers.medical);
        this.updateAuthorizationData(this.defaultAuthorizationData);
        this.setNcpdp('');
        this.setPharmacySignature('');
        this.setPrescriberSignature('');
    }

    @Action({ rawError: true })
    public async submitToAPI() {
        let response = null;
        const { contactPreferences } = this;
        try {
            const patient = {
                account: {
                    patientData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            ...this.patientInformationData,
                            middleName: '',
                            suffix: '',
                            nickname: '',
                        } as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    } as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
                    accountData: {
                        canUpdate: true,
                        canInsert: true,
                        skipSearch: false,
                        model: {
                            accountType: 1,
                            accountStatus: 1,
                            contactPreferences,
                            contacts: this.contacts,
                        },
                    } as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
                    surveySessionData: [this.survey.sessionId] as number[],
                    prescriberData: this.prescriberData,
                    siteData: this.siteData
                } as EnterpriseCoordinatorAccountUpsearchRequestModel,
                // accountAttestations: [
                //     {
                //         code: 'eServiceAttestion',
                //         startDate: moment(),
                //     },
                // ]
            } as EnrollPatientRequestModel;

            const axios: AxiosInstance = (this.patientEnrollmentApi as any).axios;
            const axiosRequest: AxiosRequestConfig = {
                url: '/edge/patientEnrollment/enrollPatient',
                method: 'POST',
                data: patient,
                headers: {
                    'x-program-id': this.settings.programId!,
                },
            };
            await Promise.all([
                (this.patientEnrollmentApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Bearer.applyToRequest(axiosRequest),
                (this.patientEnrollmentApi as any).authentications.Token.applyToRequest(axiosRequest),
            ]);
            response = await axios.request(axiosRequest);
        } catch {
            response = { status: 400 } as AxiosResponse;
        }
        return response;
    }

    public async uploadPdf(blob: Blob) {
        const formData = new FormData();
        formData.append('type', '10001');
        formData.append('attachment', blob);

        formData.append('externalPartyCode', 'CLS');
        formData.append('externalProgramCode', 'CORD');
        const axios: AxiosInstance = (this.edgeDocumentsApi as any).axios;
        const axiosRequest: AxiosRequestConfig = {
            url: 'edge/documents/v1/Document/upload',
            method: 'POST',
            data: formData,
            headers: {
                'x-program-id': this.settings.clsProgramId!,
            },
        };

        await Promise.all([
            // (this.edgeDocumentsApi as any).authentications.ApiKey.applyToRequest(axiosRequest),
            // (this.edgeDocumentsApi as any).authentications.Bearer.applyToRequest(axiosRequest),
            (this.edgeDocumentsApi as any).authentications.Token.applyToRequest(axiosRequest),
        ]);

        const result = await axios.request(axiosRequest);
        return result;
    }
}
